import { AccessPermission, ReadWriteFinancialDataAccessPermissions, ReadWritePermissions } from "../../../../../shared/permissions/types"
import { DefaultPermissions, DefaultRolePermissions } from "../../../defaults/defaults"
import { FeaturePermissionsIndex, HoldingUserPermissions, UserPermissions } from "../types"
import { hubPermissionsTemplate } from "./hub"
import { scoutPermissionsTemplate } from "./scout"

export type FeaturesPermissionsType = FeaturePermissionsTemplate<{}>["type"]

export type FeaturePermissionsTemplate<T> = {

    name: string

    /**
     * Checks if this permissions is available to a specific user
     * (If a user does not have a specific permission, then it cannot granted to them at holding level)
     */
    isAvailable: (user: UserPermissions, defaults: DefaultRolePermissions) => boolean

    /**
     * Can be used to check "parent" dependencies - i.e., other permissions that this one depends on
     * If this function is set and returns fals, then this permission will not be available
     * Serves roughly the same purpose as "isAvailable", but dynamically
     * (i.e., takes into account the holding permissions being edited, rather that static user permissions)
     */
    checkDependencies?: (permissions: FeaturePermissionsIndex) => boolean

    /**
     * This is used to define a few specific features which are defined through nested properties
     * of "main" features
     */
    nestedProperty?: {
        /** Key of the parent property */
        parent: string,
        /** Key of the nested property */
        property: string,
        /** Called to change the property's values */
        setter: (target: HoldingUserPermissions, value: {}) => void
    }

} & (
        | {
            type: "access"
            selector: (source: T) => AccessPermission
        }
        | {
            type: "readWrite"
            selector: (source: T) => ReadWritePermissions
        }
        | {
            type: "readWriteFinancial"
            selector: (source: T) => ReadWriteFinancialDataAccessPermissions
        }
    )


export type AppPermissionsTemplate<T> = {
    name: string
    permissions: {
        [permission in keyof (T)]: FeaturePermissionsTemplate<T>
    }
}

const holdingPermissionsTemplate = {
    hub: hubPermissionsTemplate,
    scout: scoutPermissionsTemplate
} as const

export type HoldingPermissionsTemplate = typeof holdingPermissionsTemplate

export function getAppPermissionsTemplate(key: keyof HoldingUserPermissions) {
    return holdingPermissionsTemplate[key]
}

const featurePermissionsTypes = {
    "access": {
        enabled: true
    },
    "readWrite": {
        read: true,
        write: true
    },
    "readWriteFinancial": {
        read: true,
        write: true,
        financialData: true
    }
}


export function getFeaturePermissionsTypeDefaults(type: FeaturesPermissionsType, value?: boolean) {

    const defaults = featurePermissionsTypes[type]

    return Object.fromEntries(Object.entries(defaults).map(([key, def]) => [key, Boolean(value ?? def)]))

}
